<template>
<v-defaults-provider :defaults="{ VMain: { scrollable: true } }">
    <v-layout ref="layoutRef" class="pane-view">
        <router-view v-slot="{ Component: View }" :route="route ?? undefined">
            <keep-alive v-if="cache">
                <component :is="route ? View : (empty ?? Blank)" />
            </keep-alive>

            <component :is="route ? View : (empty ?? Blank)" v-else />
        </router-view>
    </v-layout>
</v-defaults-provider>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { Component } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * Hosts one router-view inside its own Vuetify layout, so the page's `v-app-bar` and `v-main`
 * are sized against the pane instead of the whole viewport.
 */
const props = withDefaults(defineProps<{
    /** Route to render; `null` renders `empty` instead */
    route: RouteLocationNormalizedLoaded | null
    /** Rendered while `route` is `null` */
    empty?: Component
    /** Keep pages alive when switching between them */
    cache?: boolean
}>(), {
    empty: undefined,
    cache: true,
})

const Blank = () => null

const layoutRef = ref<{ $el: HTMLElement } | null>(null)
const scrollTops = new Map<string, number>()

const pageKey = computed(() => props.route ? String(props.route.name) : 'empty')

function getScroller () {
    return layoutRef.value?.$el.querySelector<HTMLElement>('.v-main__scroller')
}

// a page re-attached from the keep-alive cache starts scrolled to the top
watch(pageKey, (_, oldKey) => {
    const scroller = getScroller()
    if (scroller) scrollTops.set(oldKey, scroller.scrollTop)
}, { flush: 'pre' })

watch(pageKey, async key => {
    await nextTick()
    const scroller = getScroller()
    if (scroller) scroller.scrollTop = scrollTops.get(key) ?? 0
}, { flush: 'post' })
</script>

<style scoped>
.pane-view {
    height: 100%;
    background-color: rgb(var(--v-theme-background));
}
</style>
